import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  LinearProgress,
  Button,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Home as HomeIcon,
  Brush as BrushIcon,
  Receipt as ReceiptIcon,
  Agriculture as AgricultureIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as MoneyIcon,
  Token as TokenIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Project } from '../services/projectService';

interface ProjectDetailsDrawerProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
  onInvest: (project: Project) => void;
}

const ProjectDetailsDrawer: React.FC<ProjectDetailsDrawerProps> = ({
  project,
  open,
  onClose,
  onInvest,
}) => {
  const [copySuccess, setCopySuccess] = React.useState(false);

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  const getAssetIcon = (projectType: string) => {
    switch (projectType) {
      case 'real_estate':
        return <HomeIcon />;
      case 'art':
        return <BrushIcon />;
      case 'invoice':
        return <ReceiptIcon />;
      case 'agriculture':
        return <AgricultureIcon />;
      case 'equipment':
        return <BuildIcon />;
      default:
        return <AccountBalanceIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'tokenized':
        return { bg: 'linear-gradient(45deg, #3B82F6, #2FE8DD)', color: 'white' };
      case 'approved':
        return { bg: 'linear-gradient(45deg, #1E40AF, #4D1AFF)', color: 'white' };
      case 'submitted':
        return { bg: 'linear-gradient(45deg, #FEB600, #FFD54F)', color: 'black' };
      case 'rejected':
        return { bg: 'linear-gradient(45deg, #FF7D6A, #FF9B8A)', color: 'white' };
      default:
        return { bg: 'rgba(255, 255, 255, 0.1)', color: 'text.secondary' };
    }
  };

  const formatProjectType = (projectType: string) => {
    return projectType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!project) return null;

  const statusColors = getStatusColor(project.status);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: '85vh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          background: 'linear-gradient(135deg, #0A0A0A, #1A1A1A)',
          color: 'white',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B82F6' }}>
            Project Details
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Project Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #1E40AF, #3B82F6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
              }}
            >
              {getAssetIcon(project.project_type)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {project.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatProjectType(project.project_type)}
              </Typography>
            </Box>
          </Box>
          
          <Chip
            label={project.status.toUpperCase()}
            size="medium"
            sx={{
              background: statusColors.bg,
              color: statusColors.color,
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B82F6' }}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {project.description}
          </Typography>
        </Box>

        {/* Location */}
        {project.location && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B82F6' }}>
              Location
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon sx={{ fontSize: '1rem', color: '#3B82F6' }} />
              <Typography variant="body2" color="text.secondary">
                {project.location}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Key Stats */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#3B82F6' }}>
            Key Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <MoneyIcon sx={{ fontSize: 24, color: '#1E40AF', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Valuation
                </Typography>
                <Typography variant="h6" sx={{ color: '#1E40AF', fontWeight: 700 }}>
                  {formatCurrency(project.valuation_usd || 0)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <TokenIcon sx={{ fontSize: 24, color: '#3B82F6', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Tokenized
                </Typography>
                <Typography variant="h6" sx={{ color: '#3B82F6', fontWeight: 700 }}>
                  {project.tokenized_percent || 0}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Tokenization Progress
            </Typography>
            <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>
              {project.tokenized_percent || 0}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={project.tokenized_percent || 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #3B82F6, #2FE8DD)',
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Risk Analysis */}
        {project.risk_score && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#3B82F6' }}>
              Risk Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  bgcolor: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Overall Risk
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#FF7D6A', fontWeight: 700 }}>
                    {project.risk_score.score}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  bgcolor: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Liquidity
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#3B82F6', fontWeight: 700 }}>
                    {project.risk_score.liquidity_score}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Token Information */}
        {(project.token_name || project.token_symbol || project.smart_contract_address) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#3B82F6' }}>
              Token Information
            </Typography>
            <Box sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.05)', 
              p: 2, 
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              {project.token_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Token Name
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                    {project.token_name}
                  </Typography>
                </Box>
              )}
              {project.token_symbol && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Token Symbol
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                    ${project.token_symbol}
                  </Typography>
                </Box>
              )}
              {project.smart_contract_address && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Contract Address
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#FF7D6A',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        flex: 1,
                        fontSize: '0.7rem',
                      }}
                    >
                      {project.smart_contract_address}
                    </Typography>
                    <Tooltip title="Copy Address">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (project.smart_contract_address) {
                            handleCopyAddress(project.smart_contract_address);
                          }
                        }}
                        sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {copySuccess && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                      Copied!
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Action Button */}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              onInvest(project);
              onClose();
            }}
            disabled={project.status !== 'tokenized'}
            sx={{
              background: project.status === 'tokenized' 
                ? 'linear-gradient(45deg, #1E40AF, #3B82F6)' 
                : 'rgba(255, 255, 255, 0.1)',
              color: project.status === 'tokenized' ? 'white' : 'text.secondary',
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                background: project.status === 'tokenized' 
                  ? 'linear-gradient(45deg, #1E3A8A, #2563EB)' 
                  : 'rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'text.secondary',
              },
            }}
          >
            {project.status === 'tokenized' ? 'Invest Now' : 'Coming Soon'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProjectDetailsDrawer; 